import { useMemo, useState, useCallback, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import SearchIcon from "@material-ui/icons/Search";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	SelectionChangedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoadingOverlay from "../../../../../components/loaders/LazyLoading/LazyLoading";
import { ErrorModal } from "@dexteel/mesf-core";
import type { AssetQCBD } from "@/pages/asset-details/models/AssetQCBD";
import { get } from "lodash-es";
import {
	getAssetQCBD,
	removeQCBDFromAsset,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import { useSearchParamsToReducer } from "@/pages/forms/hooks";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { AssetNameRenderer } from "./components/AssetNameRenderer";
import { QCBDPropertiesModal } from "./components/properties-modal/properties-modal";
import { QCBDDownloadButton } from "./components/QCBDDownloadButton";
import { downloadQCBDFile } from "./utils/downloadQCBDFile";
import { MoveQCBDModal } from "./components/move-modal/move-QCBD-modal";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		searchToolbar: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			padding: "8px 0px",
		},
		searchField: {
			flex: 1,
			marginRight: 0,
			"& .MuiOutlinedInput-root": {
				height: "40px",
			},
		},
		gridWrapper: {
			height: "calc(100% - 60px)",
		},
	}),
);

export const AssetQCBDTable = () => {
	const navigate = useNavigate();
	const classes = useStyles();
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [searchParams] = useSearchParams();
	const resource = searchParams.get("resource") ?? "";
	const [quickFilterText, setQuickFilterText] = useState("");
	const [includeDescendants, setIncludeDescendants] = useState(true);
	const [openPropertiesDialog, setOpenPropertiesDialog] =
		useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [selectedQCBD, setSelectedQCBD] = useState<AssetQCBD | null>(null);
	const [selectedQCBDs, setSelectedQCBDs] = useState<any[]>([]);
	const [showMoveModal, setShowMoveModal] = useState(false);

	const {
		state: { assetSearchId },
		actions: { setFirstTime },
	} = useAssetDetailsContext();

	const [assetQCBDData, setAssetQCBDData] = useState<AssetQCBD[] | null>([]);
	const [error, setError] = useState<string>("");
	const [isAssetQCBDLoading, setIsAssetQCBDLoading] = useState<boolean>(false);

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const [columnDefs] = useState<ColDef[]>([
		{
			field: "",
			flex: 1,
			minWidth: 30,
			maxWidth: 30,
			filter: false,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			getQuickFilterText: () => "",
		},
		{
			field: "Type",
			headerName: "Type",
			minWidth: 100,
			maxWidth: 100,
			flex: 2,
			getQuickFilterText: (params) => params.data?.Type || "",
		},
		{
			field: "Description",
			headerName: "Description",
			minWidth: 200,
			flex: 3,
			getQuickFilterText: (params) => params.data?.Description || "",
		},
		{
			field: "AssetName",
			headerName: "Asset Name",
			minWidth: 200,
			flex: 2,
			cellRenderer: AssetNameRenderer,
			getQuickFilterText: (params) => {
				const assetName =
					params.data?.AssetName || params.data?.AssetFullPath || "";
				return assetName;
			},
		},
		{
			field: "Filename",
			headerName: "File Name",
			minWidth: 250,
			flex: 4,
			getQuickFilterText: (params) => params.data?.Filename || "",
		},
		{
			field: "Download",
			headerName: "",
			flex: 1,
			minWidth: 120,
			maxWidth: 120,
			editable: false,
			autoHeight: true,
			suppressNavigable: true,
			cellRenderer: QCBDDownloadButton,
		},
		{
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid
							item
							xs={12}
							md={12}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid item xs={12} md={12}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		},
	]);

	const searchAssetQCBD = async (includeDescendants = true) => {
		setIsAssetQCBDLoading(true);
		const res = await getAssetQCBD(assetSearchId, includeDescendants);
		if (res.ok) {
			const rowsSource = get(res, "data.tables[0].rows", []);
			setAssetQCBDData(rowsSource);
			setIsAssetQCBDLoading(false);
		} else {
			setError(res.message);
			setIsAssetQCBDLoading(false);
		}
	};

	const handleQuickFilterChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setQuickFilterText(value);
			if (gridApi) {
				gridApi.setGridOption("quickFilterText", value);
			}
		},
		[gridApi],
	);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const onRowDoubleClicked = useCallback((event: RowDoubleClickedEvent) => {
		if (event.data) {
			setSelectedQCBD(event.data);
			setOpenPropertiesDialog(true);
		}
	}, []);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			setSelectedQCBD(params.node?.data || null);

			const selectedNodes = gridApi?.getSelectedNodes();

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (params.node?.data) {
				result.push({
					name: "Properties",
					action: () => {
						setOpenPropertiesDialog(true);
						setSelectedQCBD(params.node?.data);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});

				result.push({
					name: "Go to Asset",
					action: () => {
						setFirstTime(true);
						navigate(`/asset-details/${params.node?.data.AssetId}`, {
							replace: true,
						});
					},
					icon: '<i class="fas fa-external-link-alt"></i>',
				});

				if (selectedNodes && selectedNodes.length > 1) {
					result.push({
						name: `Move to Asset (${selectedNodes.length})`,
						action: () => {
							setSelectedQCBDs(selectedNodes.map((node) => node.data));
							setShowMoveModal(true);
						},
						icon: '<i class="fas fa-exchange-alt"></i>',
					});
				} else {
					result.push({
						name: "Move to Asset",
						action: () => {
							setSelectedQCBDs([params.node?.data]);
							setShowMoveModal(true);
						},
						icon: '<i class="fas fa-exchange-alt"></i>',
					});
				}

				if (selectedNodes && selectedNodes.length > 0) {
					result.push({
						name: "Remove From Asset",
						action: () => {
							setSelectedQCBDs(selectedNodes.map((node) => node.data));
							setDeleteOpen(true);
						},
						icon: '<i class="fas fa-trash"></i>',
					});
				} else {
					result.push({
						name: "Remove From Asset",
						action: () => {
							setSelectedQCBD(params.node?.data || null);
							setSelectedQCBDs([]);
							setDeleteOpen(true);
						},
						icon: '<i class="fas fa-trash"></i>',
					});
				}

				result.push({
					name: "Download QCBD",
					action: () => {
						if (params.node?.data.URL && params.node?.data.Filename) {
							downloadQCBDFile(params.node.data.URL, params.node.data.Filename);
						}
					},
					icon: '<i class="fas fa-download"></i>',
				});
			}

			return result;
		},
		[gridApi, navigate, setFirstTime],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		setSelectedQCBDs(event.api.getSelectedNodes().map((node) => node.data));
		if (event.api.getSelectedNodes().length > 1) {
			setSelectedQCBD(null);
		} else if (event.api.getSelectedNodes().length === 1) {
			setSelectedQCBD(event.api.getSelectedNodes()[0].data);
		} else {
			setSelectedQCBD(null);
		}
	}, []);

	const removeSelected = async () => {
		if (selectedQCBDs?.length > 0) {
			for (const qcbd of selectedQCBDs) {
				const removeResult = await removeQCBDFromAsset(qcbd.QCBDRecordId);
				if (!removeResult.ok) {
					setError(removeResult.message);
				}
			}
		} else if (selectedQCBD) {
			const removeResult = await removeQCBDFromAsset(selectedQCBD.QCBDRecordId);
			if (!removeResult.ok) {
				setError(removeResult.message);
			}
		} else {
			setError("No QCBDs selected for removal.");
		}
		setDeleteOpen(false);
		await searchAssetQCBD(includeDescendants);
	};

	const handleMoveModalHide = useCallback((shouldRefresh: boolean) => {
		setShowMoveModal(false);
		setSelectedQCBDs([]);
		if (shouldRefresh) {
			searchAssetQCBD(includeDescendants);
		}
	}, []);

	useSearchParamsToReducer();

	useEffect(() => {
		// Only search if we have a valid assetSearchId (not 0, undefined, or null)
		// This prevents the initial call with assetSearchId=0 when the component first mounts
		if (resource === "qcbd" && assetSearchId && assetSearchId !== 0) {
			searchAssetQCBD(includeDescendants);
		}
	}, [resource, assetSearchId, includeDescendants]);

	return (
		<>
			<div style={containerStyle}>
				<Grid container style={gridStyle}>
					<Grid item md={12} xs={12} className={classes.agGridContainer}>
						<div className={classes.searchToolbar}>
							<TextField
								className={classes.searchField}
								variant="outlined"
								placeholder="Search QCBD files..."
								value={quickFilterText}
								onChange={handleQuickFilterChange}
								InputProps={{
									startAdornment: (
										<SearchIcon color="action" style={{ marginRight: 8 }} />
									),
								}}
								size="small"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={includeDescendants}
										onChange={(e) => setIncludeDescendants(e.target.checked)}
										color="primary"
									/>
								}
								label="Include descendant assets"
								style={{ marginLeft: 16, marginRight: 16 }}
							/>
						</div>

						<div className={classes.gridWrapper}>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={assetQCBDData}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								onRowDoubleClicked={onRowDoubleClicked}
								loadingOverlayComponent={loadingOverlayComponent}
								loading={isAssetQCBDLoading}
								quickFilterText={quickFilterText}
							/>
						</div>
					</Grid>
				</Grid>
			</div>

			<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
				<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
				<DialogContent>
					<DialogContentText style={{ padding: 0, margin: 0 }}>
						{selectedQCBDs.length > 1 ? (
							<>
								<p style={{ margin: 0 }}>REMOVE:</p>
								{selectedQCBDs.map((qcbd: AssetQCBD, index: number) => {
									return (
										<div key={qcbd.QCBDRecordId || index}>
											<p style={{ margin: "5px 0" }}>
												<b>• {qcbd.Description}, </b>
											</p>
											<p style={{ margin: 0 }}>
												FROM ASSET <b>{qcbd.AssetName}.</b>
											</p>
										</div>
									);
								})}
							</>
						) : selectedQCBD ? (
							<>
								<p style={{ margin: "0" }}>
									REMOVE <b>{selectedQCBD.Description}</b> FROM ASSET{" "}
									<b>{selectedQCBD.AssetName}?</b>
								</p>
							</>
						) : (
							<>
								<h1>
									Something went wrong: No QCBD selected for removal display.
								</h1>
							</>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setDeleteOpen(false)}
						autoFocus
						color="default"
					>
						CANCEL
					</Button>
					<Button autoFocus color="secondary" onClick={removeSelected}>
						REMOVE
					</Button>
				</DialogActions>
			</Dialog>

			<QCBDPropertiesModal
				show={openPropertiesDialog}
				onHide={(shouldUpdate) => {
					if (shouldUpdate) {
						setOpenPropertiesDialog(false);
						searchAssetQCBD(includeDescendants);
					} else {
						setOpenPropertiesDialog(false);
					}
				}}
				rowSelected={selectedQCBD}
				searchAssetQCBD={searchAssetQCBD}
			/>

			<MoveQCBDModal
				show={showMoveModal}
				selectedQCBDs={selectedQCBDs}
				onHide={handleMoveModalHide}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
